import { z, ZodError } from "zod";

const MAX_RENDER_CHART_DATA_POINTS = 2_500;

// Single series data point (backward compatible)
const SingleSeriesDataPoint = z.object({
  label: z.string().min(1, "Label is required"),
  value: z
    .number()
    .or(z.string().regex(/^\d+$/, "Value must be a number").transform(Number)),
});

// Multi-series data point: { label: string, [seriesName]: number }
// Uses record to allow dynamic series keys
const MultiSeriesDataPoint = z
  .record(z.string(), z.union([z.string(), z.number(), z.null()]))
  .refine((obj) => typeof obj.label === "string" && obj.label.length > 0, {
    message: "Each data point must have a label",
  });

export const ChartDataSchema = z.object({
  type: z.enum(["bar", "line", "pie", "area"]),
  title: z.string().optional(),
  series: z.array(z.string().min(1)).max(10).optional(), // Series names for multi-series charts
  data: z
    .array(z.union([SingleSeriesDataPoint, MultiSeriesDataPoint]))
    .min(2, "Chart must have at least 2 data points")
    .max(
      MAX_RENDER_CHART_DATA_POINTS,
      `Chart cannot have more than ${MAX_RENDER_CHART_DATA_POINTS} data points`,
    ),
});

export type T_ChartData = z.infer<typeof ChartDataSchema>;

export const E_BarSortMode = {
  DESC: "desc",
  ASC: "asc",
  ORIGINAL: "original",
} as const;

export type T_BarSortMode = (typeof E_BarSortMode)[keyof typeof E_BarSortMode];

// Type for single series data point
export type T_SingleSeriesDataPoint = z.infer<typeof SingleSeriesDataPoint>;

// Type for multi-series data point (label + dynamic series values)
export type T_MultiSeriesDataPoint = Record<string, string | number | null>;

/**
 * Check if chart data has multiple series
 */
export function isMultiSeries(data: T_ChartData): boolean {
  return Array.isArray(data.series) && data.series.length > 0;
}

/**
 * Get series names from chart data
 * Returns ["value"] for single-series charts for consistent handling
 */
export function getSeriesNames(data: T_ChartData): string[] {
  if (isMultiSeries(data) && data.series) {
    return data.series;
  }
  return ["value"];
}

/**
 * Get value for a specific series from a data point.
 * Handles both single-series and multi-series formats.
 */
function getSeriesValue(
  point: T_SingleSeriesDataPoint | T_MultiSeriesDataPoint,
  seriesName: string,
): number | null {
  if (seriesName === "value" && "value" in point) {
    return typeof point.value === "number" ? point.value : null;
  }
  const entry = Object.entries(point).find(([key]) => key === seriesName);
  if (!entry) return null;
  return typeof entry[1] === "number" ? entry[1] : null;
}

/**
 * Sort bar chart data by value.
 * Single-series: sort by value. Multi-series: sort by sum of all series values.
 * Mode controls direction: "desc" (default), "asc", or "original" (no-op).
 */
export function sortBarChartData(
  data: T_ChartData,
  mode: T_BarSortMode = E_BarSortMode.DESC,
): T_ChartData {
  if (mode === E_BarSortMode.ORIGINAL) {
    return data;
  }

  const seriesNames = getSeriesNames(data);
  const direction = mode === E_BarSortMode.ASC ? 1 : -1;

  const sorted = [...data.data].sort((a, b) => {
    const sumA = seriesNames.reduce(
      (sum, name) => sum + (getSeriesValue(a, name) ?? 0),
      0,
    );
    const sumB = seriesNames.reduce(
      (sum, name) => sum + (getSeriesValue(b, name) ?? 0),
      0,
    );
    return (sumA - sumB) * direction;
  });

  return { ...data, data: sorted };
}

/**
 * Auto-infer series names from data keys when series is not explicitly provided.
 * Looks for keys in data points that aren't "label" or "value".
 */
function inferSeriesFromData(data: T_ChartData): string[] | undefined {
  if (!data.data || data.data.length === 0) return undefined;

  const firstPoint = data.data[0];
  if (!firstPoint || typeof firstPoint !== "object") return undefined;

  // Get all keys except "label" and "value"
  const seriesKeys = Object.keys(firstPoint).filter(
    (key) => key !== "label" && key !== "value",
  );

  // Only infer if we found non-standard keys
  if (seriesKeys.length > 0) {
    return seriesKeys;
  }

  return undefined;
}

/**
 * When the model provides series names that don't match the data keys
 * (e.g. series: ["Revenue (bn USD)"] but data keys: ["Revenue"]),
 * remap the data keys to use the full series names so recharts can find them.
 */
function reconcileSeriesKeys(data: T_ChartData): T_ChartData {
  if (!data.series || data.data.length === 0) return data;

  const firstPoint = data.data[0];
  if (!firstPoint || typeof firstPoint !== "object") return data;

  const dataKeys = Object.keys(firstPoint).filter(
    (k) => k !== "label" && k !== "value",
  );

  // If all series names already exist as data keys, nothing to do
  if (data.series.every((s) => dataKeys.includes(s))) return data;

  // Try to map each series name to a data key via case-insensitive prefix match
  const mapping = new Map<string, string>(); // dataKey → seriesName
  const usedDataKeys = new Set<string>();

  for (const seriesName of data.series) {
    if (dataKeys.includes(seriesName)) {
      mapping.set(seriesName, seriesName);
      usedDataKeys.add(seriesName);
      continue;
    }
    const match = dataKeys.find(
      (dk) =>
        !usedDataKeys.has(dk) &&
        seriesName.toLowerCase().startsWith(dk.toLowerCase()),
    );
    if (match) {
      mapping.set(match, seriesName);
      usedDataKeys.add(match);
    }
  }

  // If we couldn't map every series name, fall back to inferred keys
  if (mapping.size !== data.series.length) {
    const inferred = inferSeriesFromData(data);
    if (inferred) return { ...data, series: inferred };
    return data;
  }

  // Remap data point keys to the full series names
  const remappedData = data.data.map((point) => {
    const newPoint: Record<string, string | number | null> = {};
    for (const [key, val] of Object.entries(point)) {
      const seriesName = mapping.get(key);
      newPoint[seriesName ?? key] =
        typeof val === "string" || typeof val === "number" || val === null
          ? val
          : null;
    }
    return newPoint;
  });

  return { ...data, data: remappedData };
}

export function validateChartData(code: string): {
  success: boolean;
  data?: T_ChartData;
  error?: string;
} {
  try {
    const parsed: unknown = JSON.parse(code);
    const validated = ChartDataSchema.parse(parsed);

    // Auto-infer series if not provided
    if (!validated.series) {
      const inferredSeries = inferSeriesFromData(validated);
      if (inferredSeries) {
        validated.series = inferredSeries;
      }
    }

    // Reconcile mismatched series names vs data keys
    const reconciled = reconcileSeriesKeys(validated);

    return { success: true, data: reconciled };
  } catch (error) {
    if (error instanceof ZodError) {
      const firstIssue = error.issues[0];
      const errorMessage = firstIssue
        ? `${firstIssue.message} at ${firstIssue.path.join(".")}`
        : "Validation error";
      return {
        success: false,
        error: errorMessage,
      };
    }
    if (error instanceof SyntaxError) {
      return { success: false, error: `Invalid JSON: ${error.message}` };
    }
    return { success: false, error: "Unknown validation error" };
  }
}
